import { defineElement, FC, onDidLoad, useElement, useListen } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { BPDInput, FormElement, InputElement } from '@atoms/form-elements';
import { SelectElement } from '@atoms/form-elements/select/select';
import { extendedArea, useStore } from '@bpd-library/utilities';
import { CheckboxElement } from '@source/components/atoms/form-elements/checkbox/checkbox';
import { logout } from '@source/store/user';
import { useStylesheet } from '@source/utilities/hooks';
import { DebuggerState, debugStore, getDebugState, resetState } from '@store/debugger';
import { subscribeToProjectPhase } from '@store/subscription/store';
import environment from '@utilities/environment';

import style from './development-overlay.scss';
import { clearCompare, fillCompare } from './utilities/compare';
import {
    CLASS_VISIBLE,
    GRID_COLUMNS,
    HOOK_ELEMENT,
    THEME_CLASSES,
    TOGGLE_BPDGRID__KEY,
} from './utilities/constants';
import {
    setDebugOnQueryString,
    triggerBackendDictionaryKeys,
    triggerDictionaryKeys,
} from './utilities/dictionary-keys';
import { login, loginWithAddress } from './utilities/login';
import { triggerPropertiesId } from './utilities/property-id';
import { triggerSearchObjectByID } from './utilities/search-object';
import { changeTheme, resetThemeClasses, updateBodyThemeClass } from './utilities/theme-selection';
import { triggerIdInHouseSelector } from './utilities/trigger-id-in-house-selector';
import { triggerIdInMap } from './utilities/trigger-id-in-map';
import { triggerObjectTypeInMap } from './utilities/trigger-object-type-in-map';
import { addUserAddress } from './utilities/user-address';
import { emptyWishlist, fillWishlist } from './utilities/wish-list';

const BPDGridOverlay: FC = ({ element }) => {
    const hookElement = useElement<HTMLElement>(HOOK_ELEMENT);
    const selectElement = useElement<BPDInput>('[js-hook-development-overlay-theme-selection]');
    const propertiesIdCheckboxElement = useElement<BPDInput>(
        '[js-hook-development-overlay-properties-id]',
    );
    const searchElement = useElement<BPDInput>('[js-hook-development-overlay-search-object]');

    const dictionaryKeyCheckbox = useElement<BPDInput>(
        '[js-hook-development-overlay-dictionary-keys]',
    );
    const backendDictionaryKeyCheckbox = useElement<BPDInput>(
        '[js-hook-development-overlay-backend-dictionary-keys]',
    );
    const objectTypeInMapCheckbox = useElement<BPDInput>(
        '[js-hook-development-overlay-object-type-in-map]',
    );
    const idInMapCheckbox = useElement<BPDInput>('[js-hook-development-overlay-id-in-map]');
    const idInHouseSelectorCheckbox = useElement<BPDInput>(
        '[js-hook-development-overlay-id-in-house-selector]',
    );
    const projectPhaseSelectionForm = useElement<HTMLFormElement>(
        '[js-hook-development-overlay-project-phase-selection] form',
    );
    const [debugState, subscribe] = useStore<DebuggerState>(debugStore);
    const {
        selectedTheme,
        showDictionaryKeys,
        showBackendDictionaryKeys,
        propertiesId,
        showIdInMap,
        showIdInHouseSelector,
        showObjectTypeInMap,
    } = debugState;

    useListen(document, 'keypress', (event: KeyboardEvent | null) => toggleBPDGrid(event));
    useListen(selectElement, 'change', () => changeTheme(element));
    useListen(propertiesIdCheckboxElement, 'change', triggerPropertiesId);
    useListen(searchElement, 'keypress', triggerSearchObjectByID);
    useListen(dictionaryKeyCheckbox, 'change', triggerDictionaryKeys);
    useListen(backendDictionaryKeyCheckbox, 'change', triggerBackendDictionaryKeys);
    useListen(objectTypeInMapCheckbox, 'change', triggerObjectTypeInMap);
    useListen(idInMapCheckbox, 'change', triggerIdInMap);
    useListen(idInHouseSelectorCheckbox, 'change', triggerIdInHouseSelector);
    useListen(projectPhaseSelectionForm, 'submit', async (e) => {
        e.preventDefault();

        const area = await extendedArea.getData(window.__ENVIRONMENT_DETAILS__.areaId);
        const projectPhaseId = document.querySelector<HTMLSelectElement>(
            '#select-project-phase-id',
        )?.value;
        const projectPhase = area.projectPhases.find(({ id }) => id === projectPhaseId);

        subscribeToProjectPhase({
            subscriptionInfo: {
                type: 'project',
                areaId: area.areaProject.id,
                projectPhaseId: projectPhaseId!,
                districtId: projectPhase!.districtId || '',
            },
            userInfo: {
                email: 't@test.com',
                lastName: 'lastName',
                firstName: 'firstName',
            },
            fcToken: 'XXXX.DUMMY.TOKEN.XXXX',
        });
    });

    useStylesheet(style);

    subscribe(({ selectedTheme }) => {
        resetThemeClasses();
        updateBodyThemeClass(selectedTheme);
    });

    onDidLoad(() => {
        if (getDebugState().selectedTheme) {
            resetThemeClasses();
            updateBodyThemeClass(getDebugState().selectedTheme);
        }

        setDebugOnQueryString();
    });

    function toggleBPDGrid(event: KeyboardEvent | null) {
        if (event?.code?.toLowerCase() == TOGGLE_BPDGRID__KEY.toLowerCase()) {
            hookElement.current?.classList.toggle(CLASS_VISIBLE);

            renderProjectPhaseSelection();
        }
    }

    async function renderProjectPhaseSelection() {
        const areaData = await extendedArea.getData(window.__ENVIRONMENT_DETAILS__.areaId);

        if (areaData.projectPhases) {
            projectPhaseSelectionForm.current!.innerHTML = '';
            projectPhaseSelectionForm.current!.append(
                <div>
                    <SelectElement
                        name="select-project-phase-id"
                        id="select-project-phase-id"
                        label="Select project phase ID"
                        options={areaData.projectPhases.map(({ name, id }) => ({
                            label: `${name} - ${id}`,
                            value: id,
                        }))}
                    />
                    <button type="submit">Add to subscription store</button>
                </div>,
            );
        }
    }

    function resetBPDTools() {
        resetState();
        window.location.reload();
    }

    return (
        <div class="c-development-overlay u-hidden" js-hook-development-overlay>
            <div className="o-container">
                <div class="o-grid">
                    {GRID_COLUMNS.map(() => (
                        <div class="o-col o-col-1 development-overlay__column">
                            <div className="development-overlay__column-inner"></div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="development-overlay__actions">
                {environment.isLocal && (
                    <div>
                        <button onClick={fillWishlist}>Fill wishlist</button>
                        <button onClick={emptyWishlist}>Empty wishlist</button>
                        <button onClick={addUserAddress}>Add user address</button>
                        <br />
                        <br />
                        <button onClick={login}>Login</button>
                        <button onClick={() => login({ wishlist: true })}>
                            Login with wishlist
                        </button>
                        <button onClick={() => login({ subscription: true })}>
                            Login with Subscription
                        </button>
                        <button onClick={() => loginWithAddress({ subscription: true })}>
                            Login with Address and Subscription
                        </button>
                        <button onClick={logout}>Logout</button>
                        <br />
                        <br />
                        <button onClick={fillCompare}>Fill compare</button>
                        <button onClick={clearCompare}>Clear compare</button>
                        <br />
                        <br />
                        <FormElement hook="development-overlay-project-phase-selection" />
                    </div>
                )}

                <bpd-form js-hook-development-overlay-form>
                    <InputElement
                        label="search-object"
                        id="search-object"
                        name="search-object"
                        bpdInputHook="development-overlay-search-object"
                    />
                    <SelectElement
                        label="Select theme"
                        id="select-theme"
                        name="select-theme"
                        bpdInputHook="development-overlay-theme-selection"
                        value={selectedTheme}
                        options={[
                            {
                                label: 'Default',
                                value: '',
                            },
                            ...THEME_CLASSES.map((themeClass) => ({
                                label: themeClass,
                                value: themeClass,
                            })),
                        ]}
                    />
                    <CheckboxElement
                        classes="development-overlay__checkbox"
                        value="Show dictionary keys"
                        name="dictionary-keys"
                        id="dictionary-keys"
                        label="Show dictionary keys"
                        checked={showDictionaryKeys}
                        bpdInputHook="development-overlay-dictionary-keys"
                    />
                    <CheckboxElement
                        classes="development-overlay__checkbox"
                        value="Show backend dictionary keys"
                        name="backend-dictionary-keys"
                        id="backend-dictionary-keys"
                        label="Show backend dictionary keys"
                        checked={showBackendDictionaryKeys}
                        bpdInputHook="development-overlay-backend-dictionary-keys"
                    />
                    <CheckboxElement
                        classes={`development-overlay__checkbox`}
                        value="Show object ID in properties overview"
                        name="show-object-id-in-properties-overview"
                        id="show-object-id-in-properties-overview"
                        label="Show object ID in properties overview"
                        checked={propertiesId}
                        bpdInputHook="development-overlay-properties-id"
                    />
                    <CheckboxElement
                        classes={`development-overlay__checkbox`}
                        name="show-object-type-in-map"
                        id="show-object-type-in-map"
                        label="Show object type in map"
                        checked={showObjectTypeInMap}
                        bpdInputHook="development-overlay-object-type-in-map"
                    />
                    <CheckboxElement
                        classes={`development-overlay__checkbox`}
                        name="show-id-in-map"
                        id="show-id-in-map"
                        label="Show id in map"
                        checked={showIdInMap}
                        bpdInputHook="development-overlay-id-in-map"
                    />
                    <CheckboxElement
                        classes={`development-overlay__checkbox`}
                        name="show-id-in-house-selector"
                        id="show-id-in-house-selector"
                        label="Show id in house selector"
                        checked={showIdInHouseSelector}
                        bpdInputHook="development-overlay-id-in-house-selector"
                    />
                    <button onClick={resetBPDTools}>Reset all</button>
                </bpd-form>
            </div>
        </div>
    );
};

defineElement('bpd-development-overlay', BPDGridOverlay);
